import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, File, Download, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { pluginSettingsAPI } from '../utils/api';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const UserPluginSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await pluginSettingsAPI.get();
        setSettings(response.data.data);
      } catch (error) {
        toast.error(t('pluginSettings.errors.fetch'));
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [t]);

  const handleDownload = async (fileType) => {
    try {
      const response = await pluginSettingsAPI.download(fileType);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', settings[`${fileType}File`].originalName || `${fileType}-file`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('pluginSettings.errors.download'));
    }
  };

  const renderCard = (fileType, fileData, Icon, color, subtitleKey) => (
    <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col items-center text-center max-w-xs">
      <div className={`w-20 h-20 rounded-full ${color} flex items-center justify-center shadow-md mb-4`}>
        <Icon className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-1">MetaTrader 5 Settings for 4000-5000 $</h2>
      <p className="text-sm text-gray-500 mb-4">{t(subtitleKey)}</p>

      {fileData ? (
        <>
          <div className="text-xs text-gray-500 mb-4 space-y-1">
            <p className="font-mono break-all">{fileData.originalName}</p>
            <p>{formatSize(fileData.size)}</p>
          </div>
          <button
            type="button"
            onClick={() => handleDownload(fileType)}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            {t(fileType === 'pdf' ? 'pluginSettings.downloadPdf' : 'pluginSettings.downloadSet')}
          </button>
        </>
      ) : (
        <p className="text-sm text-gray-400 italic">{t('pluginSettings.noFile')}</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('pluginSettings.title')}</h1>
        <p className="text-gray-500 text-sm mt-1">{t('pluginSettings.subtitle')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-stretch pt-8 justify-center">
        {renderCard('pdf', settings?.pdfFile, FileText, 'bg-red-500', 'pluginSettings.pdfSubtitle')}
        {renderCard('set', settings?.setFile, File, 'bg-gray-600', 'pluginSettings.setSubtitle')}
      </div>
    </div>
  );
};

export default UserPluginSettings;
